import { useEffect, useState } from "react";
import {
  Sparkles, Search, Share2, Target, Loader2, RefreshCw, Clock, History,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ContentCalendar } from "@/components/admin/ContentCalendar";
import { GscConnection } from "@/components/admin/GscConnection";
import { SocialConnection } from "@/components/admin/SocialConnection";
import { InsightHistory } from "@/components/admin/InsightHistory";
import { VIEWS } from "@/components/admin/InsightViews";
import { toast } from "sonner";

const KINDS = [
  { key: "seo", label: "SEO & Keywords", icon: Search },
  { key: "social", label: "Social Posts", icon: Share2 },
  { key: "competitors", label: "Competitors", icon: Target },
];

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

function Section({ meta, entry, busy, onGenerate, onSchedule, onHistory }) {
  const Icon = meta.icon;
  const View = VIEWS[meta.key];
  const data = entry?.data;
  const when = fmtWhen(entry?.generated_at);
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 shadow-card" data-testid={`marketing-section-${meta.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-navy"><Icon className="w-5 h-5 text-white" strokeWidth={2.2} /></span>
          <div>
            <h2 className="font-display font-bold text-lg text-brand-navy leading-tight">{meta.label}</h2>
            {when && <p className="text-xs text-brand-ink2 flex items-center gap-1"><Clock className="w-3 h-3" /> Updated {when}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onHistory(meta.key)}
            data-testid={`marketing-history-${meta.key}`}
            className="btn-focus inline-flex items-center gap-2 h-11 px-4 rounded-full border border-brand-line text-brand-navy font-bold hover:bg-brand-bg transition-colors"
          >
            <History className="w-4 h-4" /> History
          </button>
          <button
            onClick={() => onGenerate(meta.key)}
            disabled={busy}
            data-testid={`marketing-generate-${meta.key}`}
            className="btn-focus inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : data ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Generating…" : data ? "Regenerate" : "Generate"}
          </button>
        </div>
      </div>
      {busy && !data ? (
        <div className="h-40 grid place-items-center text-brand-ink2 text-sm">
          <div className="flex flex-col items-center gap-2"><Loader2 className="w-6 h-6 text-brand-red animate-spin" /> Asking the AI strategist…</div>
        </div>
      ) : data ? (
        <View d={data} onSchedule={onSchedule} />
      ) : (
        <div className="h-40 grid place-items-center text-center text-brand-ink2 text-sm px-4" data-testid={`marketing-empty-${meta.key}`}>
          Click <span className="font-bold text-brand-navy mx-1">Generate</span> to get fresh {meta.label.toLowerCase()} for the medical-alert market.
        </div>
      )}
    </div>
  );
}

export function MarketingInsights() {
  const [insights, setInsights] = useState({});
  const [busy, setBusy] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [autoRefreshRun, setAutoRefreshRun] = useState(null);
  const [savingAuto, setSavingAuto] = useState(false);
  const [posts, setPosts] = useState([]);
  const [connections, setConnections] = useState({ gsc: null, social: null });
  const [historyKind, setHistoryKind] = useState(null);

  const refreshConnections = () => {
    api.get("/admin/connections")
      .then(({ data }) => setConnections({ gsc: data.gsc || null, social: data.social || null }))
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/marketing")
      .then(({ data }) => {
        setInsights(data.insights || {});
        setAutoRefresh(!!data.auto_refresh);
        setAutoRefreshRun(data.auto_refresh_last_run || null);
      })
      .catch(() => {});
    api.get("/admin/scheduled-posts")
      .then(({ data }) => setPosts(data || []))
      .catch(() => {});
    refreshConnections();
  }, []);

  const generate = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post("/admin/marketing/generate", { kind });
      setInsights((prev) => ({ ...prev, [kind]: { data: data.data, generated_at: data.generated_at } }));
      toast.success("Fresh insights ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not generate insights."));
    } finally {
      setBusy(null);
    }
  };

  const toggleAuto = async (enabled) => {
    setAutoRefresh(enabled);
    setSavingAuto(true);
    try {
      await api.post("/admin/marketing/auto-refresh", { enabled });
      toast.success(enabled ? "Weekly auto-refresh turned on" : "Weekly auto-refresh turned off");
    } catch (err) {
      setAutoRefresh(!enabled);
      toast.error(formatApiError(err.response?.data?.detail, "Could not update auto-refresh."));
    } finally {
      setSavingAuto(false);
    }
  };

  const tomorrow9am = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  };

  const createPost = async (payload) => {
    try {
      const { data } = await api.post("/admin/scheduled-posts", payload);
      setPosts((prev) => [...prev, data]);
      toast.success("Post added to calendar");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not save post."));
    }
  };

  const scheduleFromAI = (p) => createPost({
    platform: p.platform, content: p.caption || "", hashtags: p.hashtags || [],
    scheduled_at: tomorrow9am(), status: "scheduled",
  });

  const updatePost = async (id, patch) => {
    const prev = posts;
    setPosts((ps) => ps.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    try {
      await api.patch(`/admin/scheduled-posts/${id}`, patch);
    } catch (err) {
      setPosts(prev);
      toast.error(formatApiError(err.response?.data?.detail, "Could not update post."));
    }
  };

  const deletePost = async (id) => {
    const prev = posts;
    setPosts((ps) => ps.filter((x) => x.id !== id));
    try {
      await api.delete(`/admin/scheduled-posts/${id}`);
      toast.success("Post deleted");
    } catch (err) {
      setPosts(prev);
      toast.error(formatApiError(err.response?.data?.detail, "Could not delete post."));
    }
  };

  const publishNow = async (id) => {
    try {
      const { data } = await api.post(`/admin/scheduled-posts/${id}/publish`);
      setPosts((ps) => ps.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Published to your channel!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Publishing failed."));
      try {
        const { data } = await api.get("/admin/scheduled-posts");
        setPosts(data || []);
      } catch {}
    }
  };

  const historyMeta = KINDS.find((k) => k.key === historyKind);

  return (
    <div className="space-y-5" data-testid="marketing-insights">
      <div className="rounded-2xl bg-gradient-to-r from-brand-navy to-brand-navy-700 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand-red shrink-0"><Sparkles className="w-5 h-5 text-white" /></span>
        <div className="flex-1">
          <h1 className="font-display font-bold text-xl text-white">AI Marketing Assistant</h1>
          <p className="text-white/80 text-sm mt-0.5">
            Live search & social performance plus AI insights tailored to the US medical-alert market.
          </p>
        </div>
        <div className="flex items-center gap-3 rounded-xl bg-white/10 px-3.5 py-2.5" data-testid="auto-refresh-control">
          <div className="text-right">
            <p className="text-white font-bold text-sm leading-tight">Weekly auto-refresh</p>
            <p className="text-white/70 text-xs">
              {autoRefresh ? (autoRefreshRun ? `Last run ${fmtWhen(autoRefreshRun)}` : "Refreshing soon…") : "Off"}
            </p>
          </div>
          <Switch checked={autoRefresh} onCheckedChange={toggleAuto} disabled={savingAuto} data-testid="auto-refresh-toggle" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <GscConnection conn={connections.gsc} onChanged={refreshConnections} />
        <SocialConnection conn={connections.social} onChanged={refreshConnections} />
      </div>

      {KINDS.map((meta) => (
        <Section key={meta.key} meta={meta} entry={insights[meta.key]} busy={busy === meta.key}
          onGenerate={generate} onSchedule={scheduleFromAI} onHistory={setHistoryKind} />
      ))}

      <ContentCalendar posts={posts} onCreate={createPost} onUpdate={updatePost} onDelete={deletePost} onPublishNow={publishNow} />

      <InsightHistory
        kind={historyKind}
        label={historyMeta?.label || ""}
        current={historyKind ? insights[historyKind] : null}
        open={!!historyKind}
        onOpenChange={(o) => { if (!o) setHistoryKind(null); }}
      />
    </div>
  );
}
